import { Plus, SendHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';

interface ChatRailShortcutProps {
  /** What the control does when activated — not always restoring the rail. */
  onClick: () => void;
  label?: string;
  /** Accessible name. Defaults to `label` for callers that don't repurpose the control. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Stand-in for the chat rail once it has been collapsed, anchored to the
 * bottom-left of whatever surface took its place.
 *
 * It apes the composer it replaces — same rounded accent bar, same leading
 * and trailing glyphs — so the rail reads as tucked away rather than gone.
 * The glyphs are decorative: the whole bar is one control, and there is
 * nothing to attach or send until the rail is back.
 */
export function ChatRailShortcut({
  onClick,
  label = 'Continue your conversation',
  ariaLabel = 'Show chat',
  className,
}: ChatRailShortcutProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'absolute bottom-4 left-4 z-50 flex w-80 items-center gap-1 overflow-hidden',
        'rounded-xl bg-accent/90 p-1.5 text-left shadow-lg backdrop-blur-sm',
        'transition-colors hover:bg-accent',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
    >
      <span className='flex size-9 shrink-0 items-center justify-center rounded-md'>
        <Plus className='size-4 text-muted-foreground' />
      </span>
      <span className='min-w-0 flex-1 px-2 py-1.5'>
        <span className='block truncate text-sm text-muted-foreground'>
          {label}
        </span>
      </span>
      <span className='flex size-9 shrink-0 items-center justify-center rounded-md'>
        <SendHorizontal className='size-4 text-muted-foreground' />
      </span>
    </button>
  );
}
